import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useNavigate, useParams } from 'react-router-dom';
import { navigateWithEmbedParams } from 'src/utils/navigation-utils';
import { LoadingSpinner } from 'src/components/common/LoadingSpinner';
import { BUSINESS_GROUP_CODES } from 'src/constants/business-group-constants';
import { currentUserToUserAccessForCurrentBusinessGroup } from 'src/features/business-group/access-authorization/redux/AccessAuthorizationUtils';
import {
  setUserAccessForCurrentBusinessGroup,
  setUserAccessForCurrentBusinessGroupStatus
} from 'src/features/business-group/access-authorization/redux/userAuthorizationSlice';
import { currentBusinessGroupShortDesc, selectBusinessGroupByShortDesc } from 'src/features/business-group/businessGroupSelectors';
import { fetchBusinessGroups, setCurrentBusinessGroup } from 'src/features/business-group/businessGroupSlice';
import { clearForecastTemplateCompleteData } from 'src/features/business-group/forecast-template/redux/forecastTemplateThunks';
import { Status } from 'src/models/AuthContextModels';
import { UserDataClassifications } from 'src/models/XptUsersModel';
import { fetchCampaignDimensions, fetchCampaignMetadata, fetchCountryCompanyMapping, fetchDMMCampaignDetails } from 'src/store/slices/dmmSlice';
import { selectDeferredCorpSegmentsStatus } from 'src/store/selectors/corpSegmentSelectors';
import { RootState } from 'src/store/store';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { useAuth } from './auth/AuthContextProvider';
import { fetchMasterCorpSegmentDropdownValues } from 'src/store/thunks/corpSegmentThunks';
import { canAccessXvt } from 'src/utils/xvtAccessUtils';

export const XPTBusinessGroupWrapper: React.FC = () => {
  const { dataClassificationShortDesc } = useParams<{ dataClassificationShortDesc: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);

  const businessGroupsLoaded = useSelector(
    (state: RootState) => state.businessGroupStore.businessGroups.filter((businessGroup) => businessGroup.item_metadata.is_active).length > 0
  );

  const { userAlias } = useAuth();
  const businessGroup = useSelector((state: RootState) =>
    dataClassificationShortDesc ? selectBusinessGroupByShortDesc(dataClassificationShortDesc)(state) : null
  );
  const { currentUserAccessDetails } = useSelector((state: RootState) => state.xptAccessAndAuthorizationStore);

  // Wait for deferred segments (project.json, location.json, etc.) before rendering BG routes.
  // These are loaded in background after homepage renders. If user navigates directly to a BG URL,
  // this ensures the data is ready before fetchMasterCorpSegmentDropdownValues runs.
  const deferredSegmentsStatus = useSelector(selectDeferredCorpSegmentsStatus);

  useEffect(() => {
    if (!businessGroupsLoaded) {
      dispatch(fetchBusinessGroups());
    }
  }, [businessGroupsLoaded, dispatch]);

  useEffect(() => {
    const fetchDataAndSetMetadata = async () => {
      if (!businessGroupsLoaded || !dataClassificationShortDesc || !businessGroup) {
        console.error('Navigating in Business Group Wrapper...');
        navigateWithEmbedParams(navigate, `/`);
        return;
      }

      // Wait for deferred segments to finish loading before proceeding.
      // This handles the case where a user navigates directly to a BG URL —
      // the deferred download (project.json 17MB, etc.) may still be in progress.
      if (deferredSegmentsStatus !== Status.Completed && deferredSegmentsStatus !== Status.Failed) {
        return; // Re-runs when deferredSegmentsStatus changes
      }

      try {
        const dataClassificationId = businessGroup.data_classification?.data_classification_id;
        if (!dataClassificationId) {
          console.error('No data classification ID found for the business group');
          throw new Error('No data classification ID found for the business group');
        }

        const currentUserAccessDataClassification: UserDataClassifications | undefined = currentUserAccessDetails?.user_data_classifications.find(
          (dt) => dt.data_classification_id === dataClassificationId
        );

        if (!currentUserAccessDataClassification) {
          console.error('No user data classification found for the business group');
          throw new Error('No user data classification found for the business group');
        }

        dispatch(setUserAccessForCurrentBusinessGroupStatus(Status.Loading));

        const businessGroupShortDesc = businessGroup.data_classification.data_classification_short_description;
        // DMM
        if (businessGroupShortDesc === BUSINESS_GROUP_CODES.DMM) {
          dispatch(
            fetchDMMCampaignDetails({
              dataClassificationId,
              businessGroupShortDesc
            })
          );
          dispatch(fetchCampaignMetadata());
          dispatch(fetchCampaignDimensions());
          dispatch(fetchCountryCompanyMapping());
        }

        const userAccessForCurrentBusinessGroup = currentUserToUserAccessForCurrentBusinessGroup(currentUserAccessDataClassification, userAlias);

        dispatch(setCurrentBusinessGroup(businessGroup));
        dispatch(setUserAccessForCurrentBusinessGroup(userAccessForCurrentBusinessGroup));
        dispatch(fetchMasterCorpSegmentDropdownValues({ businessGroup }));

        dispatch(setUserAccessForCurrentBusinessGroupStatus(Status.Completed));
      } catch (error) {
        console.error('Failed to fetch or set business group metadata:', error);
        navigateWithEmbedParams(navigate, `/`);
      } finally {
        setLoading(false);
      }
    };

    fetchDataAndSetMetadata();

    return () => {
      setLoading(true);
      dispatch(setCurrentBusinessGroup(null));
      dispatch(setUserAccessForCurrentBusinessGroup(null));
      dispatch(clearForecastTemplateCompleteData());
      dispatch(setUserAccessForCurrentBusinessGroupStatus(Status.NotInitiated));
    };
  }, [dataClassificationShortDesc, businessGroup, businessGroupsLoaded, deferredSegmentsStatus, dispatch, navigate]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return <Outlet />;
};

// This Wrapper deals when user is inside a Business Group.
// Will check the user type for this business group.
export const XptFinanceAdminOrFinanceOwnerWrapper: React.FC = () => {
  const { userAccessForCurrentBusinessGroup, userAccessForCurrentBusinessGroupStatus } = useSelector(
    (state: RootState) => state.xptAccessAndAuthorizationStore
  );

  if (userAccessForCurrentBusinessGroupStatus === Status.NotInitiated || userAccessForCurrentBusinessGroupStatus === Status.Loading) {
    return <LoadingSpinner />;
  }

  const isFinanceAdminOrFinanceOwner = userAccessForCurrentBusinessGroup?.isFinanceAdminOrFinanceOwner || false;

  // Navigate away if the user doesn't have access rights
  if (!isFinanceAdminOrFinanceOwner) {
    return <Navigate to="/" replace />;
  }

  // If the user has access, render the child routes
  return <Outlet />;
};

// Route guard for the xVT feature. The rule (and its LAUNCH FLIP note) lives in src/utils/xvtAccessUtils.ts.
export const XptXvtAccessWrapper: React.FC = () => {
  const { isDev } = useAuth();
  const currentBusinessGroupCode = useSelector(currentBusinessGroupShortDesc);
  const { userAccessForCurrentBusinessGroup, userAccessForCurrentBusinessGroupStatus, userCostCentersForCurrentBusinessGroup } = useSelector(
    (state: RootState) => state.xptAccessAndAuthorizationStore
  );

  if (userAccessForCurrentBusinessGroupStatus === Status.NotInitiated || userAccessForCurrentBusinessGroupStatus === Status.Loading) {
    return <LoadingSpinner />;
  }

  return canAccessXvt({
    currentBusinessGroupCode,
    isDev,
    isFinanceAdmin: userAccessForCurrentBusinessGroup?.isFinanceAdmin ?? false,
    isBudgetLeader: userAccessForCurrentBusinessGroup?.isBudgetLeader ?? false,
    isBudgetOwner: userAccessForCurrentBusinessGroup?.isBudgetOwner ?? false,
    authorizedCostCenters: userCostCentersForCurrentBusinessGroup
  }) ? (
    <Outlet />
  ) : (
    <Navigate to="/" replace />
  );
};
